import {
  Box,
  Button,
  ButtonGroup,
  Icon,
  Typography,
} from "@outschool/backpack";
import { faCalendarAlt, faChevronLeft, faChevronRight } from "@outschool/icons";
import { useLocale, useTranslation } from "@outschool/localization";
import { enrolledPath, pathnameWithoutLocale } from "@outschool/routes";
import {
  dayjs,
  formatTime,
  fullDurationStringWithTimes,
} from "@outschool/time";
import { Calendar, useReactBigCalendar } from "@outschool/ui-components-shared";
import { useActivityString } from "@outschool/ui-components-website";
import {
  IconButton,
  LegacyPopover,
  useImpressionTracking,
} from "@outschool/ui-legacy-component-library";
import { Screen } from "@outschool/ui-utils";
import _ from "lodash";
import { hasPath } from "ramda";
import React from "react";
import { ViewsProps } from "react-big-calendar";
import { useLocation } from "react-router-dom";

import * as Activity from "../../shared/Activity";
import * as Enrollment from "../../shared/Enrollment";
import * as Routes from "../../shared/Routes";
import { CalendarEvent, Slot } from "./CalendarTypes";
import Link from "./Link";

type ScheduleCalendarProps = {
  views?: ViewsProps;
  view?: string;
  date?: Date;
  events: CalendarEvent[];
  Tooltip?: React.FC<CalendarEvent>;
  Toolbar?: (arg0: any) => JSX.Element;
  eventPropGetter?: (e: CalendarEvent) => React.HTMLAttributes<unknown>;
  extraButtons?: JSX.Element[];
  loading?: boolean;
  selectable?: boolean;
  showAllHours?: boolean;
  onSelectSlot?: (slot: Slot, view: string) => void;
  onNavigate?: (date: Date) => void;
  onView?: (view: string) => void;
};

export const LoadingContext = React.createContext(false);

export function useCalendarQueryParams() {
  const location = useLocation();
  const view: string = location.query.view;
  /*
    Because ReactBigCalendar isn't timezone aware, we hardcode the date to noon so that
    any amount of timezone indiscrepancy won't matter.
  */
  let parsedDate = location.query.date
    ? dayjs(`${location.query.date} 12:00`)
    : undefined;
  if (!parsedDate || !parsedDate.isValid()) {
    parsedDate = dayjs().hour(12);
  }
  const date = parsedDate.toDate();
  return { view, date };
}

export default function ScheduleCalendar({
  views,
  view,
  date,
  events,
  Tooltip,
  Toolbar,
  eventPropGetter,
  loading = false,
  selectable,
  showAllHours,
  onSelectSlot,
  onNavigate,
  onView,
}: ScheduleCalendarProps) {
  const locale = useLocale();

  const minTime = showAllHours
    ? dayjs().startOf("day").toDate()
    : dayjs().hour(6).startOf("hour").toDate();
  const maxTime = showAllHours
    ? dayjs().endOf("day").toDate()
    : dayjs().hour(22).startOf("hour").toDate();

  const { t } = useTranslation("client\\components\\ScheduleCalendar");
  const localizedMessages = {
    date: t("Date"),
    time: t("Time"),
    event: t("Event"),
    allDay: t("All Day"),
    week: t("Week"),
    work_week: t("Work Week"),
    day: t("Day"),
    month: t("Month"),
    previous: t("Back"),
    next: t("Next"),
    yesterday: t("Yesterday"),
    tomorrow: t("Tomorrow"),
    today: t("Today"),
    agenda: t("Agenda"),
    noEventsInRange: t("There are no events in this range."),
  };

  return (
    <LoadingContext.Provider value={loading}>
      <Calendar
        culture={locale}
        events={events}
        view={view}
        date={date}
        loading={loading}
        onNavigate={onNavigate}
        onView={onView}
        min={minTime}
        max={maxTime}
        showAllHours={showAllHours}
        popup
        selectable={selectable}
        onSelectSlot={(slot: $TSFixMe) =>
          onSelectSlot && view && onSelectSlot(slot, view)
        }
        views={views}
        components={{
          toolbar: Toolbar,
          event: (event: { event: CalendarEvent }) => (
            <Event event={event.event} view={view} />
          ),
        }}
        messages={localizedMessages}
        eventPropGetter={eventPropGetter}
        tooltipAccessor={(event: CalendarEvent) => {
          if (!Tooltip) {
            return "";
          }

          const studentName = Enrollment.studentNamesString([event.enrollment]);
          if (studentName) {
            return `${event.title} for ${studentName}`;
          }

          return event.title;
        }}
      />
    </LoadingContext.Provider>
  );
}

function eventTime(time: dayjs.ConfigType, timeZone: string): string {
  return formatTime(time, timeZone, false);
}

function Event({ event, view }: { event: CalendarEvent; view?: string }) {
  const { t } = useTranslation("client\\components\\ScheduleCalendar");
  const { durationString } = useActivityString();
  const timeZone = dayjs.tz.guess(); // Big Calendar won't allow to position events on a different timezone from the browser.
  const { allDay, start, title, enrollment, section } = event;
  const [open, setOpen] = React.useState(false);
  const location = useLocation();

  const dayOfWeek = dayjs(start).day();
  const isWeekend = dayOfWeek === 5 || dayOfWeek === 6; // Friday or Saturday

  const mainPlacement =
    isWeekend || Activity.isFlexSchedule(section.activity) ? "bottom" : "right";
  const placement =
    isWeekend || Activity.isFlexSchedule(section.activity)
      ? "top-right"
      : "right";

  const { trackTouch: trackHover } = useImpressionTracking({
    uniqueId: "schedule_calendar_event",
    trackingLabel: "schedule_calendar_event",
    trackingEventName: "schedule_calendar_event_hover",
  });

  const noOfEnrolledLearners = event.section?.filledSpaceCount ?? 0;

  return (
    <>
      <LegacyPopover
        positionFixed={true}
        isHidden={!open}
        mainPlacement={open ? mainPlacement : "bottom"} // this is to trigger a redraw every open in case the size of the calendar changes
        placement={placement}
        innerProps={{
          sx: { marginLeft: "20px", padding: "10px" },
        }}
        parentOverflow={{ enabled: false }}
        animationDuration="0s" // To avoid triggering onMouseLeave which will close the popover.
        renderButton={ref => (
          <Box
            ref={ref}
            sx={{
              height: view === "week" ? "100%" : "auto",
            }}
            onMouseEnter={() => {
              setOpen(true);
              trackHover();
            }}
            onMouseLeave={() => {
              setOpen(false);
            }}
          >
            <Link
              trackingName="calendar_event"
              to={
                enrollment
                  ? enrolledPath(section.uid)
                  : ["/teach/schedule", "/organization/schedule"].includes(
                      pathnameWithoutLocale(location.pathname),
                    )
                  ? Routes.sectionLearnersPath(section)
                  : Routes.activityPath(section.activity)
              }
              target="_blank"
            >
              {view === "month" ? (
                <>
                  {!allDay && <span>{eventTime(start, timeZone)} </span>}
                  <Typography
                    variant="inherit"
                    sx={{
                      fontWeight: "fontWeightBold",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {title}
                  </Typography>{" "}
                </>
              ) : (
                <>
                  <Typography
                    variant="inherit"
                    sx={{
                      fontWeight: "fontWeightBold",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {title}
                  </Typography>{" "}
                  {!allDay && <span>{eventTime(start, timeZone)} </span>}
                  {enrollment && Enrollment.studentNamesString([enrollment])}
                </>
              )}
            </Link>
          </Box>
        )}
      >
        <Box
          flex
          sx={{
            flexFlow: "column",
            maxWidth: "327px",
            whiteSpace: "break-spaces",
            gap: "6px",
            padding: "8px",
          }}
        >
          <Box
            sx={{
              color: "gray",
            }}
          >
            {Activity.formatString(event.section.activity)}
            {Activity.format(event.section.activity) ===
              Activity.ACTIVITY_FORMAT.MULTI_DAY &&
              // eslint-disable-next-line i18next/no-literal-string
              hasPath(["meeting", "sectionSequenceNumber"], event) &&
              ` • ${event.meeting?.sectionSequenceNumber} of ${event.section.meetingCount}`}
          </Box>
          <Typography
            variant="h4"
            sx={{
              color: "common.black",
            }}
          >
            {title}
          </Typography>
          {enrollment && (
            <Typography variant="body1">
              {Enrollment.studentNamesString([enrollment])}
            </Typography>
          )}
          {!event.section.activity.hasTeacherSchedule && (
            <Box
              sx={{
                color: "gray",
              }}
            >
              <Icon icon={faCalendarAlt} />{" "}
              {durationString(event.section.activity)}
            </Box>
          )}
          <Box
            sx={{
              color: "gray",
            }}
          >
            {fullDurationStringWithTimes(
              event.start,
              event.end,
              timeZone,
              false,
            )}
          </Box>
          {!isNaN(noOfEnrolledLearners) && (
            <Box
              sx={{
                color: "gray",
              }}
            >
              {t("{{noOfEnrolledLearners}} learner", {
                noOfEnrolledLearners: noOfEnrolledLearners,
                count: noOfEnrolledLearners,
                defaultValue_plural: "{{noOfEnrolledLearners}} learners",
              })}
            </Box>
          )}
        </Box>
      </LegacyPopover>
    </>
  );
}

export function DateSelector({
  view,
  date,
  label,
  userTimeZone,
  localizer,
  onNavigate,
}: $TSFixMe) {
  const { t } = useTranslation("client\\components\\ScheduleCalendar");
  const { Navigate } = useReactBigCalendar();
  const isMobile = Screen.useIsMobile();
  const dateLabel =
    view === "agenda" && userTimeZone
      ? t("{{startDate}} onward", {
          startDate: dayjs(date).tz(userTimeZone).format("ll"),
        })
      : label;

  return (
    <Box
      flex
      sx={{
        alignItems: "center",
        gap: "0.5em",
      }}
    >
      <Button onClick={() => Navigate && onNavigate(Navigate.TODAY)}>
        {localizer.messages.today}
      </Button>
      <IconButton
        size="large"
        icon={faChevronLeft}
        onClick={() => Navigate && onNavigate(Navigate.PREVIOUS)}
      />
      <IconButton
        data-test-id="calendar-next-button"
        size="large"
        icon={faChevronRight}
        onClick={() => Navigate && onNavigate(Navigate.NEXT)}
      />
      {!isMobile && <Typography variant="h4">{dateLabel}</Typography>}
    </Box>
  );
}

export enum ViewSelectorName {
  Week = "week",
  Month = "month",
  Agenda = "agenda",
}

export function ViewSelector({
  views,
  view,
  onView,
  localizer = null,
}: {
  views: string[];
  view: string | undefined;
  onView: (viewName: string) => void;
  localizer?: { messages: { [key: string]: string } } | null;
}) {
  return (
    <Box
      flex
      sx={{
        alignItems: "center",
      }}
    >
      <ButtonGroup variant="outlined">
        {views.map(viewName => (
          <Button
            key={viewName}
            variant={viewName === view ? "contained" : "outlined"}
            onClick={() => onView(viewName)}
          >
            {(localizer && localizer.messages[viewName]) ||
              _.upperFirst(viewName)}
          </Button>
        ))}
      </ButtonGroup>
    </Box>
  );
}
